import {isUncertainResponse} from './idempotency.mjs';
let mutations = 0;
let installing = false;
const uncertain = new Set();
export const hasPendingOperations = () => mutations > 0 || uncertain.size > 0;
const notify = () => {if(globalThis.window?.dispatchEvent && window.Event) window.dispatchEvent(new window.Event('mesomb:update-safety'));};
export function beginMutation() {
  if (installing) throw new Error('An application update is being installed. Please wait.');
  mutations += 1;notify();
  let done = false;
  const anonymous = Symbol();
  return (response, key) => {
    if (done) return;
    done = true; mutations -= 1;
    const operation = key || anonymous;
    if (!response || isUncertainResponse(response)) uncertain.add(operation);
    else uncertain.delete(operation);
    notify();
  };
}
export function updateBlockReason(path = globalThis.location?.pathname || '/') {
  if (globalThis.navigator?.onLine === false) return 'offline';
  if (mutations || uncertain.size) return 'operation';
  // Preserve the entire public payment journey, including confirmation polling and redirects.
  if (/^\/(?:pay|l|f|e|s|c|checkout)(?:\/|$)/i.test(path) || /(?:^|\/)(?:withdrawal|refill|bulkpayments?|bulk|secure|securepayments?|wallets?|scheduled)(?:\/|$)/i.test(path)) return 'payment';
  return undefined;
}
export async function safelyInstallUpdate(action) {
  const reason = updateBlockReason();
  if (reason || installing) throw new Error(reason || 'installing');
  installing = true;
  try { await action(); }
  catch (error) { installing = false; throw error; }
}
export function requestWorkerActivation(worker) {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => {channel.port1.close(); reject(new Error('failed'));}, 10000);
    channel.port1.onmessage = event => {
      clearTimeout(timer);channel.port1.close();
      if (event.data?.accepted === true) resolve();
      else reject(new Error(event.data?.reason === 'tabs' ? 'tabs' : 'failed'));
    };
    try {worker.postMessage({type:'MESOMB_ACTIVATE_UPDATE'}, [channel.port2]);}
    catch {clearTimeout(timer);channel.port1.close();reject(new Error('failed'));}
  });
}
export function waitForActivation(worker) {
  return new Promise((resolve, reject) => {
    const finish = () => {
      if (worker.state === 'activated') {cleanup();resolve();}
      else if (worker.state === 'redundant') {cleanup();reject(new Error('failed'));}
    };
    const timer = setTimeout(() => {cleanup();reject(new Error('failed'));}, 15000);
    const cleanup = () => {clearTimeout(timer);worker.removeEventListener('statechange', finish);};
    worker.addEventListener('statechange',finish);finish();
  });
}
export function waitForWaitingWorker(registration, timeout = 15000) {
  if (registration.waiting) return Promise.resolve(registration.waiting);
  return new Promise((resolve, reject) => {
    let worker;
    const cleanup = () => {
      clearTimeout(timer);
      registration.removeEventListener('updatefound', onUpdateFound);
      worker?.removeEventListener('statechange', onStateChange);
    };
    const finish = () => {
      if (registration.waiting) {cleanup();resolve(registration.waiting);}
    };
    const onStateChange = () => {
      if (worker?.state === 'redundant') {cleanup();reject(new Error('failed'));return;}
      // The registration is updated immediately after this event in most browsers,
      // but defer one turn for engines that publish `waiting` afterwards.
      queueMicrotask(finish);
    };
    const watch = candidate => {
      worker?.removeEventListener('statechange', onStateChange);
      worker = candidate;
      worker?.addEventListener('statechange', onStateChange);
      finish();
    };
    const onUpdateFound = () => watch(registration.installing);
    const timer = setTimeout(() => {cleanup();reject(new Error('failed'));}, timeout);
    registration.addEventListener('updatefound', onUpdateFound);
    watch(registration.installing);
  });
}
export function validVersionPolicy(value) {
  const version = candidate => typeof candidate === 'string' && /^\d+\.\d+\.\d+$/.test(candidate);
  return Boolean(value && version(value.latest_version) && version(value.minimum_supported_version) &&
    (value.force_update === undefined || typeof value.force_update === 'boolean') &&
    (value.message === undefined || typeof value.message === 'string' && value.message.length <= 1000));
}
